import { useState, useEffect, useCallback, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
// Toaster (sonner) dimuat selepas render pertama: keluarkan ~33 KB dari main bundle
const Toaster = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
// Init Sentry selepas load (lazy import, keluarkan dari main bundle untuk LCP)
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || "";
if (SENTRY_DSN) {
  window.addEventListener("load", () => {
    setTimeout(() => {
      import("@sentry/react").then((Sentry) => {
        Sentry.init({ dsn: SENTRY_DSN, environment: "production", tracesSampleRate: 0.2 });
      });
    }, 2000);
  });
}

// Critical: landing loads immediately; app pages lazy-load (smaller public bundle = faster LCP)
import LandingPage from "@/pages/LandingPage";
const AppShell = lazy(() => import("@/components/AppShell"));
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getVisitorId, scheduleTrackVisit } from "@/lib/attribution";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const NewOrderPage = lazy(() => import("@/pages/NewOrderPage"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));

// Lazy-loaded pages (less frequently visited, load on-demand)
const SignUpPage = lazy(() => import("@/pages/SignUpPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const AddFundsPage = lazy(() => import("@/pages/AddFundsPage"));
const MassOrderPage = lazy(() => import("@/pages/MassOrderPage"));
const TicketsPage = lazy(() => import("@/pages/TicketsPage"));
const AIToolsPage = lazy(() => import("@/pages/AIToolsPage"));
const AdminServicesPage = lazy(() => import("@/pages/admin/AdminServicesPage"));
const AdminOrdersPage = lazy(() => import("@/pages/admin/AdminOrdersPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/AdminUsersPage"));
const AdminFundsPage = lazy(() => import("@/pages/admin/AdminFundsPage"));
const AdminProviderPage = lazy(() => import("@/pages/admin/AdminProviderPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const PackagesPage = lazy(() => import("@/pages/PackagesPage"));
const KedaiRealMyPage = lazy(() => import("@/pages/KedaiRealMyPage"));
const GuidePage = lazy(() => import("@/pages/GuidePage"));
const ReferralPage = lazy(() => import("@/pages/ReferralPage"));
const AdminAnalyticsPage = lazy(() => import("@/pages/admin/AdminAnalyticsPage"));
const AdminPackagesPage = lazy(() => import("@/pages/admin/AdminPackagesPage"));
const AdminCouponsPage = lazy(() => import("@/pages/admin/AdminCouponsPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminTicketsPage = lazy(() => import("@/pages/admin/AdminTicketsPage"));
const AdminRefillsPage = lazy(() => import("@/pages/admin/AdminRefillsPage"));
const AdminBlogPage = lazy(() => import("@/pages/admin/AdminBlogPage"));
const AdminEmailBroadcastPage = lazy(() => import("@/pages/admin/AdminEmailBroadcastPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const UserStatsPage = lazy(() => import("@/pages/UserStatsPage"));
const ApiPage = lazy(() => import("@/pages/ApiPage"));
const SMSBlastPage = lazy(() => import("@/pages/SMSBlastPage"));
const AdminTrafficPage = lazy(() => import("@/pages/admin/AdminTrafficPage"));
const GoRedirect = lazy(() => import("@/pages/GoRedirect"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const PublicReviewPage = lazy(() => import("@/pages/PublicReviewPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const SMMPanelMalaysiaPage = lazy(() => import("@/pages/SMMPanelMalaysiaPage"));
const BeliServicePage = lazy(() => import("@/pages/BeliServicePage"));
const KeywordLandingPage = lazy(() => import("@/pages/KeywordLandingPage"));
const RealEngagementPage = lazy(() => import("@/pages/RealEngagementPage"));
const TopicHubPage = lazy(() => import("@/pages/TopicHubPage"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const EditorialPolicyPage = lazy(() => import("@/pages/EditorialPolicyPage"));
const LiveChatWidget = lazy(() => import("@/components/LiveChatWidget"));

// Dedicated SEO keyword landing pages (exact-match URLs)
const SEO_LANDING_SLUGS = [
  "beli-followers-tiktok-malaysia",
  "beli-followers-instagram-malaysia",
  "beli-views-tiktok-malaysia",
  "beli-likes-instagram-malaysia",
  "beli-subscribers-youtube-malaysia",
  "beli-followers-facebook-malaysia",
];

// Loading fallback for lazy pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-[3px] border-violet-200 border-t-violet-600 animate-spin" />
        <p className="text-sm text-zinc-400">Loading...</p>
      </div>
    </div>
  );
}

// Prefetch remaining lazy chunks after initial render (hanya untuk pengguna log masuk; pelawat awam tak perlu chunk app)
function usePrefetchChunks(enabled) {
  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(() => {
      // Prefetch commonly visited pages after 3s idle
      import("@/pages/AddFundsPage");
      import("@/pages/TicketsPage");
      import("@/pages/ReferralPage");
      import("@/pages/PackagesPage");
    }, 3000);
    return () => clearTimeout(timer);
  }, [enabled]);
}

// Widget live chat dimuat selepas interaksi pertama atau 4s idle (bukan dalam laluan kritikal LCP/TTI)
function DeferredLiveChat() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    const t = setTimeout(go, 4000);
    const evs = ["pointerdown", "keydown", "scroll", "touchstart"];
    evs.forEach(e => window.addEventListener(e, go, { once: true, passive: true }));
    return () => { clearTimeout(t); evs.forEach(e => window.removeEventListener(e, go)); };
  }, []);
  if (!ready) return null;
  return <Suspense fallback={null}><LiveChatWidget /></Suspense>;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API helper
export const apiCall = async (method, url, data = null, token = null) => {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    const response = await axios({ method, url: `${API}${url}`, data, headers });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Network error' };
  }
};

// Safe storage - Safari can throw on localStorage access (private mode / blocked storage)
export const safeStorage = {
  get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* storage blocked */ } },
  remove: (k) => { try { localStorage.removeItem(k); } catch (e) { /* storage blocked */ } },
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(safeStorage.get('flexa_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    safeStorage.remove('flexa_token');
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (token) {
      apiCall('get', '/auth/me', null, token)
        .then(data => { setUser(data.user); setLoading(false); })
        .catch(() => { logout(); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token, logout]);

  const login = useCallback((newToken, userData) => {
    safeStorage.set('flexa_token', newToken);
    setToken(newToken);
    setUser(userData);
    // Kaitkan pelawat → user untuk laporan sumber traffic (senyap jika gagal)
    apiCall('post', '/track/identify', { vid: getVisitorId() }, newToken).catch(() => {});
  }, []);

  useEffect(() => { scheduleTrackVisit(); }, []);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const data = await apiCall('get', '/auth/me', null, token);
        setUser(data.user);
      } catch(e) { /* ignore */ }
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-muted-foreground">Loading...</div></div>;
  if (!user) return <Navigate to="/" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  usePrefetchChunks(!!user);

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/signup" element={user ? <Navigate to="/dashboard" replace /> : <SignUpPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/blog/topik/:cluster" element={<TopicHubPage />} />
      <Route path="/penulis/mr-blackcardx" element={<AuthorPage />} />
      <Route path="/polisi-editorial" element={<EditorialPolicyPage />} />
      <Route path="/smm-panel-malaysia" element={<SMMPanelMalaysiaPage />} />
      <Route path="/beli/:slug" element={<BeliServicePage />} />
      {SEO_LANDING_SLUGS.map(s => <Route key={s} path={`/${s}`} element={<KeywordLandingPage />} />)}
      {SEO_LANDING_SLUGS.map(s => <Route key={`alias-${s}`} path={`/${s.replace('-malaysia', '')}`} element={<Navigate to={`/${s}`} replace />} />)}
      <Route path="/real-malaysian-engagement" element={<RealEngagementPage />} />
      <Route path="/engagement-malaysia" element={<Navigate to="/real-malaysian-engagement" replace />} />

      {/* User routes */}
      <Route path="/dashboard" element={<ProtectedRoute><AppShell><DashboardPage /></AppShell></ProtectedRoute>} />
      <Route path="/new-order" element={<ProtectedRoute><AppShell><NewOrderPage /></AppShell></ProtectedRoute>} />
      <Route path="/kedai-real-my" element={<ProtectedRoute><AppShell><KedaiRealMyPage /></AppShell></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><AppShell><OrdersPage /></AppShell></ProtectedRoute>} />
      <Route path="/panduan" element={<ProtectedRoute><AppShell><GuidePage /></AppShell></ProtectedRoute>} />
      <Route path="/guide" element={<Navigate to="/panduan" replace />} />
      <Route path="/add-funds" element={<ProtectedRoute><AppShell><AddFundsPage /></AppShell></ProtectedRoute>} />
      <Route path="/topup" element={<ProtectedRoute><AppShell><AddFundsPage /></AppShell></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><AppShell><PaymentSuccessPage /></AppShell></ProtectedRoute>} />
      <Route path="/mass-order" element={<ProtectedRoute><AppShell><MassOrderPage /></AppShell></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><AppShell><TicketsPage /></AppShell></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AppShell><ProfilePage /></AppShell></ProtectedRoute>} />
      <Route path="/ai-tools" element={<ProtectedRoute><AppShell><AIToolsPage /></AppShell></ProtectedRoute>} />
      <Route path="/packages" element={<ProtectedRoute><AppShell><PackagesPage /></AppShell></ProtectedRoute>} />
      <Route path="/referral" element={<ProtectedRoute><AppShell><ReferralPage /></AppShell></ProtectedRoute>} />
      <Route path="/my-stats" element={<ProtectedRoute><AppShell><UserStatsPage /></AppShell></ProtectedRoute>} />
      <Route path="/reseller-api" element={<ProtectedRoute><AppShell><ApiPage /></AppShell></ProtectedRoute>} />
      <Route path="/sms-blast" element={<ProtectedRoute><AppShell><SMSBlastPage /></AppShell></ProtectedRoute>} />
      <Route path="/review-manager" element={<ProtectedRoute><AppShell><ReviewsPage /></AppShell></ProtectedRoute>} />
      <Route path="/review/:reviewCode" element={<PublicReviewPage />} />

      {/* Admin routes */}
      <Route path="/admin/services" element={<ProtectedRoute adminOnly><AppShell><AdminServicesPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute adminOnly><AppShell><AdminOrdersPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AppShell><AdminUsersPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/funds" element={<ProtectedRoute adminOnly><AppShell><AdminFundsPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/provider" element={<ProtectedRoute adminOnly><AppShell><AdminProviderPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AppShell><AdminAnalyticsPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/packages" element={<ProtectedRoute adminOnly><AppShell><AdminPackagesPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AppShell><AdminSettingsPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/tickets" element={<ProtectedRoute adminOnly><AppShell><AdminTicketsPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/refills" element={<ProtectedRoute adminOnly><AppShell><AdminRefillsPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/coupons" element={<ProtectedRoute adminOnly><AppShell><AdminCouponsPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute adminOnly><AppShell><AdminBlogPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/email-broadcast" element={<ProtectedRoute adminOnly><AppShell><AdminEmailBroadcastPage /></AppShell></ProtectedRoute>} />
      <Route path="/admin/email-template" element={<Navigate to="/admin/email-broadcast" replace />} />

      <Route path="/go/:slug" element={<GoRedirect />} />
      <Route path="/admin/traffic" element={<ProtectedRoute adminOnly><AppShell><AdminTrafficPage /></AppShell></ProtectedRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

import { HelmetProvider } from 'react-helmet-async';

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <DeferredLiveChat />
            <Suspense fallback={null}><Toaster position="top-right" richColors /></Suspense>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
